import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("le_admin_token"));
  const [email, setEmail] = useState(() => localStorage.getItem("le_admin_email"));
  const [role, setRole] = useState(() => localStorage.getItem("le_admin_role") || "admin");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/auth/me");
        if (!cancelled) {
          setRole(res.data.role || "admin");
          localStorage.setItem("le_admin_role", res.data.role || "admin");
        }
      } catch (err) {
        // Token invalid/expired — fall through to logout. Logged for diagnostics.
        if (!cancelled) {
          console.warn("Auth check failed, clearing session:", err?.response?.status || err?.message);
          localStorage.removeItem("le_admin_token");
          localStorage.removeItem("le_admin_email");
          localStorage.removeItem("le_admin_role");
          setToken(null);
          setEmail(null);
          setRole("admin");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const acceptToken = async (newToken, newEmail) => {
    localStorage.setItem("le_admin_token", newToken);
    localStorage.setItem("le_admin_email", newEmail);
    setToken(newToken);
    setEmail(newEmail);
    try {
      const me = await api.get("/auth/me", { headers: { Authorization: `Bearer ${newToken}` } });
      localStorage.setItem("le_admin_role", me.data.role || "admin");
      setRole(me.data.role || "admin");
    } catch (err) {
      console.warn("Failed to fetch role after login:", err?.response?.status || err?.message);
    }
  };

  /**
   * Step 1 of login. Returns one of:
   *   { kind: "ok" }                                 — authenticated, token stored
   *   { kind: "mfa", mfaToken, email }               — must call mfaVerify next (TOTP)
   *   { kind: "setup", mfaToken, email }             — must call mfaSetupBegin / mfaSetupConfirm next (TOTP)
   *   { kind: "sms", smsToken, email, phoneMasked }  — must call smsLoginVerify next (SMS 2FA)
   *   { kind: "smsSetup", smsToken, email }          — must call smsSetupStart / smsSetupConfirm next
   */
  const login = async (em, password) => {
    const res = await api.post("/auth/login", { email: em, password });
    const data = res.data;
    if (data.token) {
      await acceptToken(data.token, data.email);
      return { kind: "ok" };
    }
    if (data.sms_login_required) {
      return { kind: "sms", smsToken: data.sms_session_token, email: data.email, phoneMasked: data.phone_masked };
    }
    if (data.sms_setup_required) {
      return { kind: "smsSetup", smsToken: data.sms_session_token, email: data.email };
    }
    if (data.mfa_required) {
      return { kind: "mfa", mfaToken: data.mfa_session_token, email: data.email };
    }
    if (data.mfa_setup_required) {
      return { kind: "setup", mfaToken: data.mfa_session_token, email: data.email };
    }
    throw new Error("Unexpected login response");
  };

  /** Returns { secret, otpauth_uri, email } for the QR display. */
  const mfaSetupBegin = async (mfaToken) => {
    const res = await api.post("/auth/mfa/setup", { mfa_session_token: mfaToken });
    return res.data;
  };

  /** Finishes setup. On success stores token. */
  const mfaSetupConfirm = async (mfaToken, code) => {
    const res = await api.post("/auth/mfa/enable", { mfa_session_token: mfaToken, code });
    await acceptToken(res.data.token, res.data.email);
  };

  /** Step 2 of normal TOTP login. On success stores token. */
  const mfaVerify = async (mfaToken, code) => {
    const res = await api.post("/auth/mfa/verify", { mfa_session_token: mfaToken, code });
    await acceptToken(res.data.token, res.data.email);
  };

  /** SMS 2FA — first-time setup: send code to a phone. */
  const smsSetupStart = async (smsToken, phone) => {
    const res = await api.post("/auth/sms/setup/start", { sms_session_token: smsToken, phone });
    return res.data; // { ok, phone_masked }
  };

  /** SMS 2FA — first-time setup: confirm the code, save the phone, get JWT. */
  const smsSetupConfirm = async (smsToken, phone, code) => {
    const res = await api.post("/auth/sms/setup/confirm", { sms_session_token: smsToken, phone, code });
    await acceptToken(res.data.token, res.data.email);
  };

  /** SMS 2FA — normal login: resend the code to the saved phone. */
  const smsLoginResend = async (smsToken) => {
    const res = await api.post("/auth/sms/login/resend", { sms_session_token: smsToken });
    return res.data;
  };

  /** SMS 2FA — normal login: verify the code, get JWT. */
  const smsLoginVerify = async (smsToken, code) => {
    const res = await api.post("/auth/sms/login/verify", { sms_session_token: smsToken, code });
    await acceptToken(res.data.token, res.data.email);
  };

  const logout = () => {
    localStorage.removeItem("le_admin_token");
    localStorage.removeItem("le_admin_email");
    localStorage.removeItem("le_admin_role");
    setToken(null);
    setEmail(null);
    setRole("admin");
  };

  return (
    <AuthCtx.Provider value={{
      token, email, role, loading,
      login, logout, mfaSetupBegin, mfaSetupConfirm, mfaVerify,
      smsSetupStart, smsSetupConfirm, smsLoginResend, smsLoginVerify,
      isAuthed: !!token,
    }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
